import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { LucideIcon } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'

/**
 * "You are not looking at your own workspace."
 *
 * One banner for every borrowed-identity mode: admin impersonation, partner
 * delegation, auditor preview, and the employee view still to come. They are the
 * same message with a different subject, and they had three separate
 * implementations with three visual languages before this:
 *
 *   - `AdminModeBanner` — full-width top bar, `from-amber-500 to-orange-500`
 *   - `PartnerDelegationBanner` — full-width `Alert`, `bg-blue-50`
 *   - `AdminAuditorViewBanner` — tinted the PAGE HEADER instead, `#FFF8E0`,
 *     and had to be opted into per route (4 call sites for 18 routes)
 *
 * All three hard-coded raw palette values, which `web/CLAUDE.md` rule 1 forbids
 * because they break dark mode. This one uses tokens.
 *
 * ## Height is a contract, not a guess
 *
 * ~61 surfaces sized themselves from `calc(100vh - <header>)` — the viewport
 * minus page chrome, hard-coded — so every one of them overflowed by the
 * banner's height the moment a banner appeared. Sixty-one copies of an
 * assumption a single element could invalidate.
 *
 * Now two variables with one job each:
 *
 *   --app-vh       viewport MINUS any banner. Declared `100svh` at `:root` in
 *                  index.css; the workspace shell narrows it while a banner shows.
 *   --app-chrome-h header + tab-bar height. Nothing to do with banners.
 *
 * Page chrome subtracts the chrome from `--app-vh` and never mentions the
 * viewport or the banner. Adding a second banner is arithmetic in one place.
 *
 * Dialogs, sheets, popovers and image `max-h` caps deliberately still use the
 * viewport: they are `position: fixed` and genuinely span it, banner or not.
 */

/** Fixed so the height budget can be computed without measuring the DOM. */
export const VIEW_MODE_BANNER_HEIGHT = '2rem'

/**
 * Page chrome above a full-height panel: the page header plus the tab bar. Was a
 * bare `98px` in three places.
 *
 * Deliberately EXCLUDES the banner. Panels use
 * `calc(var(--app-vh) - var(--app-chrome-h, 98px))`, and `--app-vh` has already
 * taken the banner out — counting it here too would subtract it twice and leave
 * the panel 2rem short.
 */
export const BASE_CHROME_HEIGHT = '98px'

export type ViewModeTone = 'amber' | 'blue'

const TONE: Record<ViewModeTone, { wrap: string; label: string }> = {
  // Impersonation / preview — you are seeing someone else's view.
  amber: {
    wrap: 'border-status-orange/30 bg-status-orange-bg',
    label: 'text-status-orange',
  },
  // Delegation — you are acting on someone else's behalf, legitimately.
  blue: {
    wrap: 'border-status-blue/30 bg-status-blue-bg',
    label: 'text-status-blue',
  },
}

export interface ViewModeBannerProps {
  icon: LucideIcon
  /** Short mode name, e.g. "Auditor preview". Always visible. */
  label: string
  /** What the mode means, and whose data is shown. */
  description: ReactNode
  /** The way out. Always visible — a mode with no exit is a trap. */
  action: ReactNode
  tone?: ViewModeTone
}

export function ViewModeBanner({
  icon: Icon,
  label,
  description,
  action,
  tone = 'amber',
}: ViewModeBannerProps) {
  const styles = TONE[tone]
  return (
    <div
      role='status'
      style={{ height: VIEW_MODE_BANNER_HEIGHT }}
      // `relative z-20` keeps it above the sidebar container (`z-10`): a mode
      // indicator that another surface can cover is not an indicator.
      className={`relative z-20 flex w-full shrink-0 items-center gap-3 border-b px-4 ${styles.wrap}`}
    >
      <span
        className={`inline-flex h-4 shrink-0 items-center gap-1.5 text-[11px] leading-4 font-semibold uppercase tracking-[0.12em] ${styles.label}`}
      >
        <Icon className='block h-3.5 w-3.5 shrink-0' />
        {label}
      </span>
      {/* Never responsively hidden. The predecessor made this `hidden lg:inline`,
          so on a narrow window the only remaining signal was a small link in the
          far corner — the mode indicator vanished exactly where the screen was
          most crowded and the mistake most likely. */}
      <span className='min-w-0 flex-1 truncate text-[12px] leading-4 text-text-body'>
        {description}
      </span>
      <span className='inline-flex shrink-0 items-center'>{action}</span>
    </div>
  )
}


// ── Publishing ────────────────────────────────────────────────────────

/**
 * Which borrowed identity is in play. Order below is precedence.
 *
 * More than one can be true at once — a site admin in admin mode, delegated
 * from a partner org, previewing the auditor's view — so the registry keys by
 * source and the slot shows the most privileged one. `PartnerDelegationBanner`
 * already encoded this rule in a comment ("Admin mode takes precedence and
 * shows AdminModeBanner instead"); now it is enforced in one place instead of
 * being each banner's job to check the others.
 */
export type ViewModeSource = 'admin' | 'delegation' | 'preview' | 'employee'

const SOURCE_PRECEDENCE: readonly ViewModeSource[] = [
  'admin', // impersonating an organisation — the broadest powers, say so loudest
  'delegation', // acting for a client org as a partner
  'preview', // seeing your own data as someone else sees it
  'employee', // in the employee self-service portal rather than the admin surface
]

/**
 * Active banners by source.
 *
 * The banner must span the ENTIRE window — sidebar included — so it cannot be
 * rendered by the component that knows the mode: those all live inside
 * `SidebarProvider`, which is a horizontal flex, so anything inside it starts to
 * the right of the sidebar. (`AdminModeBanner` had exactly that bug for as long
 * as it existed.)
 *
 * So knowledge and position are split: whoever can determine a mode PUBLISHES
 * here, and a single `<ViewModeBannerSlot />` above `SidebarProvider` renders the
 * winner. Employee View gets full-window placement by publishing one entry.
 */
export const viewModeBannersAtom = atom<
  Partial<Record<ViewModeSource, ViewModeBannerProps>>
>({})

/** The banner that should be on screen, honouring precedence. */
export const viewModeBannerAtom = atom(get => {
  const active = get(viewModeBannersAtom)
  for (const source of SOURCE_PRECEDENCE) {
    const banner = active[source]
    if (banner) return banner
  }
  return null
})

/**
 * Publish a banner for as long as this component is mounted.
 *
 * `deps` control when the payload is re-published; it holds ReactNode, so it
 * cannot be compared structurally.
 */
export function usePublishViewModeBanner(
  source: ViewModeSource,
  banner: ViewModeBannerProps | null,
  deps: unknown[],
) {
  const setBanners = useSetAtom(viewModeBannersAtom)
  useEffect(() => {
    setBanners(prev => {
      if (!banner) {
        if (!(source in prev)) return prev
        const { [source]: _dropped, ...rest } = prev
        return rest
      }
      return { ...prev, [source]: banner }
    })
    // Clear on unmount, or the banner outlives the mode — a stale "you are
    // impersonating" claim is worse than no claim.
    return () =>
      setBanners(prev => {
        if (!(source in prev)) return prev
        const { [source]: _dropped, ...rest } = prev
        return rest
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBanners, source, ...deps])
}

/** Renders the winning banner. Mount ONCE, above the sidebar wrapper. */
export function ViewModeBannerSlot() {
  const banner = useAtomValue(viewModeBannerAtom)
  if (!banner) return null
  return <ViewModeBanner {...banner} />
}

/**
 * Viewport height minus any view-mode banner.
 *
 * The app shell sizes itself with `h-screen` / `min-h-svh`, which are
 * viewport-absolute and know nothing about a banner above them — the same class
 * of bug as the `calc(100vh - 98px)` panels, one level up. Surfaces that must
 * fill the window below the banner use this instead.
 */
export function appViewportHeight(bannerCount = 0): string {
  return bannerCount > 0
    ? `calc(100svh - ${bannerCount} * ${VIEW_MODE_BANNER_HEIGHT})`
    : '100svh'
}

/**
 * Height of the banner strip, as a top offset for FIXED descendants.
 *
 * The sidebar container is `fixed top-[var(--app-banner-h,0px)]`. Without this
 * it anchored to the viewport top and drew straight over the banner's left
 * portion — the banner looked full-width but its label and the start of its
 * text were hidden behind the sidebar.
 */
export function appBannerHeight(bannerCount = 0): string {
  return bannerCount > 0
    ? `calc(${bannerCount} * ${VIEW_MODE_BANNER_HEIGHT})`
    : '0px'
}
